/**
 * Firebase Email/Password Login Component
 *
 * This component provides email/password authentication using Firebase.
 * It's designed to be modular and can be extended to support additional
 * authentication methods.
 *
 * Features:
 * - Email/Password sign-in
 * - Email/Password sign-up (new users)
 * - Password reset functionality
 * - Error handling with user-friendly messages
 * - Loading states
 *
 * Extensibility:
 * This component can be extended to support additional auth providers:
 * - Google: Add a GoogleAuthProvider button
 * - Facebook: Add a FacebookAuthProvider button
 * - Phone: Add phone number authentication
 *
 * See firebase.ts for configuration instructions.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithPopup,
  signInWithRedirect,
  signInWithCredential,
  getRedirectResult,
  signOut,
  GoogleAuthProvider,
  OAuthProvider,
  Auth,
  UserCredential,
  AuthError,
  updateProfile,
} from 'firebase/auth';
import { auth, isFirebaseConfigured } from '../config/firebase';
import { isNative } from '../config/platform';
import { BRAND } from '../brand';
import { sendVerificationEmail } from '../utils/authApi';

interface FirebaseLoginProps {
  onSuccess?: (idToken: string) => void;
  onError?: (error: Error) => void;
}

type AuthMode = 'signin' | 'signup' | 'reset';

/** Providers offered through the native iOS account sheets. */
type NativeProvider = 'google' | 'apple';

// Type guard to check if error is a Firebase AuthError
const isFirebaseAuthError = (error: unknown): error is AuthError => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    typeof (error as AuthError).code === 'string'
  );
};

/**
 * Sign in through a native iOS account sheet and finish the sign-in in the WEB
 * Firebase SDK.
 *
 * Why a plugin at all: signInWithPopup cannot open a popup inside a WKWebView, and
 * the signInWithRedirect fallback in handleGoogleSignIn is equally dead there — it
 * navigates away from the app origin, capacitor://localhost can never be an
 * authorized Firebase redirect URI, and Google refuses embedded webviews outright
 * (disallowed_useragent).
 *
 * skipNativeAuth is the load-bearing flag. Left at its default (false) the plugin
 * signs into the *native* Firebase iOS SDK and the JS SDK's auth.currentUser stays
 * null — which silently breaks getIdToken(), onAuthStateChanged and token refresh
 * even though the sign-in "worked". With it on, the plugin only hands back the raw
 * OAuth credential and we complete the sign-in in JS, so every existing caller
 * keeps working unchanged. It is passed per call as well as being set in
 * capacitor.config.ts, so a missing or stale config block can't silently flip it.
 */
const signInWithNativeProvider = async (
  authInstance: Auth,
  provider: NativeProvider
): Promise<UserCredential> => {
  // Dynamic import keeps the plugin (and its Capacitor bridge) out of the web bundle.
  const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');

  const result =
    provider === 'google'
      ? await FirebaseAuthentication.signInWithGoogle({ skipNativeAuth: true })
      : await FirebaseAuthentication.signInWithApple({ skipNativeAuth: true });

  const idToken = result.credential?.idToken;
  if (!idToken) {
    throw new Error(`${provider} sign-in returned no ID token.`);
  }

  const webCredential =
    provider === 'google'
      ? GoogleAuthProvider.credential(idToken)
      : // Apple signs a hash of the nonce. The plugin generates the nonce, sends
        // SHA-256(nonce) to Apple and returns the RAW one here, which is exactly
        // what the web SDK needs to verify the token.
        new OAuthProvider('apple.com').credential({
          idToken,
          rawNonce: result.credential?.nonce,
        });

  const userCredential = await signInWithCredential(authInstance, webCredential);

  // Apple releases the user's name only on the very first authorization, and never
  // inside the ID token — so signInWithCredential leaves displayName null and the
  // backend would create the player nameless. Copy it across while we still have it.
  const nativeDisplayName = result.user?.displayName;
  if (nativeDisplayName && !userCredential.user.displayName) {
    try {
      await updateProfile(userCredential.user, { displayName: nativeDisplayName });
    } catch (profileError) {
      // Non-fatal: the account exists, it just has no name yet.
      console.error('Failed to copy native displayName onto the Firebase user:', profileError);
    }
  }

  return userCredential;
};

/**
 * Backing out of a native account sheet rejects the plugin call. Google's message
 * says "canceled"; Apple surfaces ASAuthorizationError 1001. Neither is a failure
 * worth a red banner.
 */
const isNativeSignInCancellation = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /cancell?ed/i.test(message) || message.includes('1001');
};

/**
 * Apple's Hide My Email relay domain. Firebase stores the relay address verbatim
 * as the user's email, and the backend matches rostered players by email — so a
 * player who is on a roster as greg@gmail.com would land in a brand-new, empty
 * account instead of their own.
 */
const APPLE_PRIVATE_RELAY_DOMAIN = '@privaterelay.appleid.com';

const isPrivateRelayEmail = (email: string | null | undefined): boolean =>
  !!email && email.toLowerCase().endsWith(APPLE_PRIVATE_RELAY_DOMAIN);

// The warning is a one-time decision per account, not a nag: Firebase keeps
// returning the relay address on every subsequent sign-in, so remember that the
// user already chose to go ahead with it.
const relayAckKey = (uid: string): string => `apple_relay_ack:${uid}`;

const hasAcknowledgedPrivateRelay = (uid: string): boolean => {
  try {
    return localStorage.getItem(relayAckKey(uid)) === '1';
  } catch {
    return false;
  }
};

const acknowledgePrivateRelay = (uid: string): void => {
  try {
    localStorage.setItem(relayAckKey(uid), '1');
  } catch {
    // Storage unavailable (private mode) — worst case the user is asked again.
  }
};

const FirebaseLogin: React.FC<FirebaseLoginProps> = ({ onSuccess, onError }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mode, setMode] = useState<AuthMode>('signin');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);
  // Set when an Apple sign-in came back with a Hide My Email relay address and the
  // user has not yet decided what to do about it. Holds the completed Firebase
  // credential so "continue anyway" can finish without a second trip through Apple.
  const [relayCredential, setRelayCredential] = useState<UserCredential | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldownUntil, setResendCooldownUntil] = useState(0);
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0);

  useEffect(() => {
    if (resendCooldownUntil <= Date.now()) {
      setResendSecondsLeft(0);
      return;
    }
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((resendCooldownUntil - Date.now()) / 1000));
      setResendSecondsLeft(remaining);
    };
    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, [resendCooldownUntil]);

  // Completes a Google sign-in that used the redirect fallback (when
  // signInWithPopup hit auth/popup-blocked). Firebase persists the pending
  // result; calling getRedirectResult on mount picks it up after the
  // browser returns from Google's OAuth page. No-ops if no redirect was
  // in flight. Skipped on native, where there is no redirect flow to resume —
  // the plugin path never leaves the WebView.
  const redirectHandledRef = useRef(false);
  useEffect(() => {
    if (!auth || isNative() || redirectHandledRef.current) return;
    redirectHandledRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const credential = await getRedirectResult(auth);
        if (cancelled || !credential) return;
        setIsGoogleLoading(true);
        await handleAuth(credential, true);
      } catch (err: unknown) {
        if (cancelled) return;
        const errorCode = isFirebaseAuthError(err) ? err.code : '';
        const errorMessage = isFirebaseAuthError(err) ? err.message : (err instanceof Error ? err.message : '');
        setError(getErrorMessage(errorCode, errorMessage));
        onError?.(err instanceof Error ? err : new Error(String(err)));
      } finally {
        if (!cancelled) setIsGoogleLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const switchMode = useCallback((newMode: AuthMode) => {
    setMode(newMode);
    setError(null);
    setSuccessMessage(null);
    setPassword('');
    setConfirmPassword('');
    setName('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setUnverifiedEmail(null);
  }, []);

  // Check if Firebase is configured
  if (!isFirebaseConfigured()) {
    return null;
  }

  // Runtime, not build-time: the iOS bundle is also what `npm start` serves in a
  // desktop browser, where the plugin isn't there to drive the Apple sheet.
  const showAppleSignIn = isNative();

  const getErrorMessage = (errorCode: string, errorMessage?: string): string => {
    // Handle missing initial state error (redirect state issue)
    if (errorMessage && errorMessage.includes('missing initial state')) {
      return 'Authentication session expired. Please try signing in again.';
    }
    if (errorCode === 'auth/missing-or-invalid-nonce' || errorCode === 'auth/argument-error') {
      if (errorMessage && errorMessage.includes('missing initial state')) {
        return 'Authentication session expired. Please try signing in again.';
      }
    }

    switch (errorCode) {
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/user-disabled':
        return 'This account has been disabled. Please contact support.';
      case 'auth/user-not-found':
        return 'No account found with this email. Please sign up first.';
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again.';
      case 'auth/invalid-credential':
        return 'Invalid email or password. Please try again.';
      case 'auth/email-already-in-use':
        return 'An account with this email already exists. Please sign in.';
      case 'auth/weak-password':
        return 'Password is too weak. Please use at least 6 characters.';
      case 'auth/password-does-not-meet-requirements':
        // Log the full error for debugging
        console.error('Firebase password requirements error:', errorMessage);
        
        // Try to extract specific requirements from the error message
        if (errorMessage) {
          // Handle different error message formats - match content inside square brackets
          // Example: "Missing password requirements: [Password must contain a numeric character]"
          let requirementsMatch = errorMessage.match(/\[(.*?)\]/);
          
          if (requirementsMatch && requirementsMatch[1]) {
            const requirementsList: string[] = [];
            const rawRequirements = requirementsMatch[1];
            
            // Log what we extracted for debugging
            console.log('Extracted requirements from error:', rawRequirements);
            
            // Split by comma if multiple requirements, otherwise use the whole string
            const individualRequirements = rawRequirements.split(',').map(r => r.trim());
            
            // Extract individual requirements and translate them to user-friendly messages
            for (const req of individualRequirements) {
              // Check for specific requirement patterns
              if (req.match(/numeric|number/i)) {
                requirementsList.push('at least one number');
              } else if (req.match(/uppercase/i)) {
                requirementsList.push('at least one uppercase letter');
              } else if (req.match(/lowercase/i)) {
                requirementsList.push('at least one lowercase letter');
              } else if (req.match(/non-alphanumeric|special|symbol/i)) {
                requirementsList.push('at least one special character');
              } else if (req.match(/length|characters? long|at least \d+/i)) {
                const lengthMatch = req.match(/at least (\d+)|(\d+) character/i);
                if (lengthMatch) {
                  const length = lengthMatch[1] || lengthMatch[2];
                  requirementsList.push(`at least ${length} characters long`);
                }
              }
            }
            
            // Remove duplicates
            const uniqueRequirements = Array.from(new Set(requirementsList));
            
              if (uniqueRequirements.length > 0) {
                // Format requirements nicely
                let formattedRequirements = '';
                if (uniqueRequirements.length === 1) {
                  formattedRequirements = uniqueRequirements[0];
                } else if (uniqueRequirements.length === 2) {
                  formattedRequirements = uniqueRequirements.join(' and ');
                } else {
                  formattedRequirements = uniqueRequirements.slice(0, -1).join(', ') + ', and ' + uniqueRequirements[uniqueRequirements.length - 1];
                }
                
                // Note: Firebase may only show one missing requirement at a time
                // Even if this requirement seems to be met, check all common requirements
                return `Password does not meet requirements. Your password must contain: ${formattedRequirements}. Please also ensure your password has at least 8 characters, including uppercase and lowercase letters.`;
              }
          }
        }
        // Fallback: show generic message with common requirements
        // Firebase may only show one missing requirement at a time, so list all common ones
        return 'Password does not meet requirements. Please ensure your password contains at least 8 characters, including uppercase letters, lowercase letters, and numbers.';
      case 'auth/too-many-requests':
        return 'Too many failed attempts. Please try again later.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection.';
      case 'auth/popup-closed-by-user':
        return 'Sign-in popup was closed. Please try again.';
      case 'auth/cancelled-popup-request':
        return 'Only one popup request is allowed at a time.';
      case 'auth/popup-blocked':
        return 'Popup was blocked by your browser. Please allow popups and try again.';
      case 'auth/account-exists-with-different-credential':
        // Firebase's default "one account per email address" setting. Common once
        // Apple/Google sign-in exists alongside the original email/password accounts.
        return 'An account already exists with this email address. Please sign in the way you signed up — with your email and password, or with the other provider.';
      case 'auth/operation-not-allowed':
        // The provider is not enabled in Firebase Console -> Authentication.
        return 'This sign-in method is not available right now. Please try another option.';
      default:
        return 'An error occurred. Please try again.';
    }
  };

  const handleAuth = async (credential: UserCredential, skipEmailVerificationCheck: boolean = false) => {
    try {
      // Check if email is verified before proceeding (skip for Google OAuth as emails are auto-verified)
      if (!skipEmailVerificationCheck && !credential.user.emailVerified) {
        setError('Please verify your email address before signing in. Check your inbox for a verification email from Firebase.');
        setUnverifiedEmail(credential.user.email || null);
        return;
      }

      // Get the Firebase ID token
      const idToken = await credential.user.getIdToken();
      onSuccess?.(idToken);
    } catch (err: any) {
      const error = err as Error;
      setError('Failed to get authentication token. Please try again.');
      onError?.(error);
    }
  };

  const handleResendVerification = async () => {
    if (!unverifiedEmail) return;
    if (Date.now() < resendCooldownUntil) return;

    setIsResending(true);
    setSuccessMessage(null);
    try {
      await sendVerificationEmail(unverifiedEmail, 'resend');
      setResendCooldownUntil(Date.now() + 60_000);
      setSuccessMessage('A verification email has been sent. Please check your inbox and click the verification link.');
    } catch (verificationError: any) {
      const status = verificationError?.response?.status;
      const detail = verificationError?.response?.data?.detail || verificationError?.message;
      if (status === 429) {
        setResendCooldownUntil(Date.now() + 60_000);
        setError('Too many verification email requests. Please check your inbox (including spam) or try again in a few minutes.');
      } else {
        setError(detail || 'Failed to send verification email. Please try again later.');
      }
      console.error('Failed to send verification email:', verificationError);
    } finally {
      setIsResending(false);
    }
  };

  // Native sign-in surfaces plugin/native errors, not Firebase AuthErrors, so they
  // rarely carry an auth/* code. Log the real thing and show the friendly fallback.
  const handleNativeSignInError = (err: unknown) => {
    if (isNativeSignInCancellation(err)) return;

    const errorCode = isFirebaseAuthError(err) ? err.code : '';
    const errorMessage = isFirebaseAuthError(err)
      ? err.message
      : err instanceof Error
        ? err.message
        : '';
    console.error('Native sign-in error:', { code: errorCode, message: errorMessage, fullError: err });
    setError(getErrorMessage(errorCode, errorMessage));
    onError?.(err instanceof Error ? err : new Error(String(err)));
  };

  const handleGoogleSignIn = async () => {
    if (!auth) {
      setError('Firebase is not initialized. Please check your configuration.');
      return;
    }

    setIsGoogleLoading(true);
    setError(null);
    setSuccessMessage(null);
    setUnverifiedEmail(null);

    if (isNative()) {
      try {
        const credential = await signInWithNativeProvider(auth, 'google');
        // Google emails are automatically verified, so skip the email verification check
        await handleAuth(credential, true);
      } catch (err: unknown) {
        handleNativeSignInError(err);
      } finally {
        setIsGoogleLoading(false);
      }
      return;
    }

    // Build the provider up front so the catch block can reuse it for the
    // redirect fallback when popups are blocked.
    const provider = new GoogleAuthProvider();
    provider.addScope('email');
    provider.addScope('profile');

    try {
      // Clean up any stale Firebase redirect state before attempting sign-in
      // This prevents "missing initial state" errors
      const { cleanupFirebaseRedirectState } = await import('../config/firebase');
      cleanupFirebaseRedirectState();

      const credential = await signInWithPopup(auth, provider);
      // Google emails are automatically verified, so skip the email verification check
      await handleAuth(credential, true);
    } catch (err: unknown) {
      const errorCode = isFirebaseAuthError(err) ? err.code : '';
      const errorMessage = isFirebaseAuthError(err) ? err.message : (err instanceof Error ? err.message : '');

      // If the browser blocked the popup (or the environment doesn't support
      // popups, e.g. in-app webviews), fall back to the redirect flow. This
      // navigates away to Google and returns to /login, where the mounted
      // getRedirectResult handler completes the sign-in.
      if (errorCode === 'auth/popup-blocked' || errorCode === 'auth/operation-not-supported-in-this-environment') {
        try {
          await signInWithRedirect(auth, provider);
          // signInWithRedirect navigates away; nothing else to do here.
          return;
        } catch (redirectErr: unknown) {
          const redirectErrorCode = isFirebaseAuthError(redirectErr) ? redirectErr.code : '';
          const redirectErrorMessage = isFirebaseAuthError(redirectErr) ? redirectErr.message : (redirectErr instanceof Error ? redirectErr.message : '');
          setError(getErrorMessage(redirectErrorCode, redirectErrorMessage));
          onError?.(redirectErr instanceof Error ? redirectErr : new Error(String(redirectErr)));
          return;
        }
      }

      // If we get a "missing initial state" error, clean up state and retry once
      if (errorMessage && errorMessage.includes('missing initial state')) {
        try {
          const { cleanupFirebaseRedirectState } = await import('../config/firebase');
          cleanupFirebaseRedirectState();

          // Retry once after cleanup (reuse the provider from the outer scope)
          const credential = await signInWithPopup(auth, provider);
          await handleAuth(credential, true);
          return; // Success, exit early
        } catch (retryErr: unknown) {
          // If retry also fails, fall through to show error
          const retryErrorCode = isFirebaseAuthError(retryErr) ? retryErr.code : '';
          const retryErrorMessage = isFirebaseAuthError(retryErr) ? retryErr.message : (retryErr instanceof Error ? retryErr.message : '');
          const message = getErrorMessage(retryErrorCode, retryErrorMessage);
          setError(message);
          onError?.(retryErr instanceof Error ? retryErr : new Error(String(retryErr)));
          return;
        }
      }
      
      const message = getErrorMessage(errorCode, errorMessage);
      setError(message);
      onError?.(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    if (!auth) {
      setError('Firebase is not initialized. Please check your configuration.');
      return;
    }

    setIsAppleLoading(true);
    setError(null);
    setSuccessMessage(null);
    setUnverifiedEmail(null);
    setRelayCredential(null);

    try {
      const credential = await signInWithNativeProvider(auth, 'apple');

      // Hide My Email would hand the backend a relay address that matches nobody on
      // the roster, quietly producing an empty second account. Make it a decision.
      if (
        isPrivateRelayEmail(credential.user.email) &&
        !hasAcknowledgedPrivateRelay(credential.user.uid)
      ) {
        setRelayCredential(credential);
        return;
      }

      // Apple verifies the address before it ever reaches Firebase.
      await handleAuth(credential, true);
    } catch (err: unknown) {
      handleNativeSignInError(err);
    } finally {
      setIsAppleLoading(false);
    }
  };

  const handleRejectPrivateRelay = async () => {
    setRelayCredential(null);
    // The app never received a JWT, but the web SDK is signed in — drop it so
    // auth.currentUser doesn't disagree with the app's logged-out state.
    try {
      if (auth) await signOut(auth);
    } catch (signOutError) {
      console.error('Failed to sign out after declining Hide My Email:', signOutError);
    }
    setSuccessMessage(
      'Signed back out. Please sign in with the email address your captain used to add you to the team.'
    );
  };

  const handleAcceptPrivateRelay = async () => {
    const credential = relayCredential;
    if (!credential) return;

    acknowledgePrivateRelay(credential.user.uid);
    setRelayCredential(null);
    await handleAuth(credential, true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setUnverifiedEmail(null);

    if (!auth) {
      setError('Firebase is not initialized. Please check your configuration.');
      return;
    }

    if (mode === 'signup') {
      if (!name || name.trim().length === 0) {
        setError('Please enter your name.');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
    }

    if (mode !== 'reset' && password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setIsLoading(true);

    try {
      // Clean up any stale Firebase redirect state before attempting sign-in
      // This prevents "missing initial state" errors
      const { cleanupFirebaseRedirectState } = await import('../config/firebase');
      cleanupFirebaseRedirectState();
      
      if (mode === 'signin') {
        const credential = await signInWithEmailAndPassword(auth, email, password);
        await handleAuth(credential);
      } else if (mode === 'signup') {
        const credential = await createUserWithEmailAndPassword(auth, email, password);
        // Update user profile with displayName
        try {
          await updateProfile(credential.user, {
            displayName: name.trim(),
          });
        } catch (profileError) {
          console.error('Failed to update user profile:', profileError);
          // Continue even if profile update fails
        }
        // Send verification email after signup using backend endpoint
        try {
          await sendVerificationEmail(credential.user.email || email, 'signup');
          setSuccessMessage('Account created! Please check your email to verify your account before signing in.');
          setMode('signin');
        } catch (verificationError: any) {
          // If sending verification email fails, still allow signup but warn user
          const errorMessage = verificationError?.response?.data?.detail || verificationError?.message || 'Unknown error';
          console.error('Failed to send verification email:', {
            error: verificationError,
            message: errorMessage,
            status: verificationError?.response?.status,
            data: verificationError?.response?.data
          });
          setSuccessMessage(`Account created! However, we could not send a verification email: ${errorMessage}. Please try signing in and verify your email when prompted.`);
          setMode('signin');
        }
        // Don't call handleAuth for signup - user needs to verify email first
      } else if (mode === 'reset') {
        // Configure custom action URL for password reset
        const actionCodeSettings = {
          url: `${window.location.origin}/auth-action`,
          handleCodeInApp: false,
        };
        await sendPasswordResetEmail(auth, email, actionCodeSettings);
        setSuccessMessage('Password reset email sent. Check your inbox.');
        setMode('signin');
      }
    } catch (err: unknown) {
      const errorCode = isFirebaseAuthError(err) ? err.code : '';
      const errorMessage = isFirebaseAuthError(err) ? err.message : (err instanceof Error ? err.message : '');
      
      // If we get a "missing initial state" error, clean up state and retry once
      if (errorMessage && errorMessage.includes('missing initial state')) {
        try {
          const { cleanupFirebaseRedirectState } = await import('../config/firebase');
          cleanupFirebaseRedirectState();
          
          // Retry once after cleanup (only for signin mode)
          if (mode === 'signin') {
            const credential = await signInWithEmailAndPassword(auth, email, password);
            await handleAuth(credential);
            return; // Success, exit early
          }
        } catch (retryErr: unknown) {
          // If retry also fails, fall through to show error
          const retryErrorCode = isFirebaseAuthError(retryErr) ? retryErr.code : '';
          const retryErrorMessage = isFirebaseAuthError(retryErr) ? retryErr.message : (retryErr instanceof Error ? retryErr.message : '');
          const message = getErrorMessage(retryErrorCode, retryErrorMessage);
          setError(message);
          onError?.(retryErr instanceof Error ? retryErr : new Error(String(retryErr)));
          return;
        }
      }
      
      // Log full error details for debugging password requirements
      console.error('Firebase authentication error:', {
        code: errorCode,
        message: errorMessage,
        fullError: err,
      });
      
      const message = getErrorMessage(errorCode, errorMessage);
      setError(message);
      onError?.(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full space-y-4">
      {/* Sign in with Apple.
          Required by App Store guideline 4.8 wherever Google sign-in is offered, and
          Apple checks that it is no less prominent — so it shares the Google button's
          size, weight and position in the stack. Colours follow Apple's branding rules
          (black on light backgrounds, white on dark).

          Native only: the web build would additionally need an Apple Services ID and
          signing key configured in the Firebase console before this could work at all,
          and guideline 4.8 does not apply to the website. */}
      {showAppleSignIn && (
        <button
          type="button"
          onClick={handleAppleSignIn}
          disabled={isAppleLoading || isGoogleLoading || isLoading}
          className="w-full py-2.5 px-4 bg-black hover:bg-gray-800 disabled:bg-gray-500 dark:bg-white dark:hover:bg-gray-100 dark:disabled:bg-gray-400 text-white dark:text-black font-medium rounded-lg transition-colors flex items-center justify-center shadow-sm"
          data-testid="apple-signin-button"
        >
          {isAppleLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Signing in with Apple...
            </>
          ) : (
            <>
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
              </svg>
              Continue with Apple
            </>
          )}
        </button>
      )}

      {/* Google Sign-In Button */}
      <button
        type="button"
        onClick={handleGoogleSignIn}
        disabled={isGoogleLoading || isAppleLoading || isLoading}
        className="w-full py-2.5 px-4 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:bg-gray-100 dark:disabled:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 font-medium rounded-lg transition-colors flex items-center justify-center shadow-sm"
        data-testid="google-signin-button"
      >
        {isGoogleLoading ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-gray-600 dark:text-gray-300" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Signing in with Google...
          </>
        ) : (
          <>
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </>
        )}
      </button>

      {/* Hide My Email interception.
          Apple hands Firebase a @privaterelay.appleid.com address. The backend
          matches rostered players by email, so posting this token would create a
          second, empty account instead of signing the player into their own. We
          cannot silently refuse either — Apple forbids penalising users who hide
          their address — so both outcomes are offered explicitly. */}
      {relayCredential && (
        <div
          className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg text-amber-900 dark:text-amber-100 text-sm space-y-3"
          role="alert"
          data-testid="apple-private-relay-warning"
        >
          <p className="font-semibold">Apple hid your email address</p>
          <p>
            Apple gave us <span className="break-all font-medium">{relayCredential.user.email}</span>{' '}
            instead of your real address. Captains add players to {BRAND.name} by email, so
            continuing would start a brand-new account with no teams and no schedule, and our
            emails to you would bounce.
          </p>
          <p>
            Sign in with the address your captain used instead. To keep using Apple, first open
            Settings &rarr; your name &rarr; Sign in with Apple, select this app, choose
            &ldquo;Stop Using Apple ID&rdquo;, then sign in again and pick &ldquo;Share My
            Email&rdquo;.
          </p>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={handleRejectPrivateRelay}
              className="w-full py-2 px-4 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors"
              data-testid="apple-relay-use-team-email"
            >
              Use my team email instead
            </button>
            <button
              type="button"
              onClick={handleAcceptPrivateRelay}
              className="w-full py-2 px-4 text-amber-900 dark:text-amber-100 underline font-medium"
              data-testid="apple-relay-continue"
            >
              Continue anyway and start a new account
            </button>
          </div>
        </div>
      )}

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 font-medium">OR</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Email Input */}
        <div>
          <label htmlFor="firebase-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Email
          </label>
          <input
            id="firebase-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-colors"
            placeholder="you@example.com"
            disabled={isLoading}
            data-testid="firebase-email-input"
          />
        </div>

        {/* Name Input (signup only) */}
        {mode === 'signup' && (
          <div>
            <label htmlFor="firebase-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Name
            </label>
            <input
              id="firebase-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-colors"
              placeholder="Your full name"
              disabled={isLoading}
              data-testid="firebase-name-input"
            />
          </div>
        )}

        {/* Password Input (hidden for reset mode) */}
        {mode !== 'reset' && (
          <div>
            <label htmlFor="firebase-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Password
            </label>
            <div className="relative">
              <input
                id="firebase-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-colors"
                placeholder="••••••••"
                disabled={isLoading}
                data-testid="firebase-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none"
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        )}

        {/* Confirm Password (signup only) */}
        {mode === 'signup' && (
          <div>
            <label htmlFor="firebase-confirm-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Confirm Password
            </label>
            <div className="relative">
              <input
                id="firebase-confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-colors"
                placeholder="••••••••"
                disabled={isLoading}
                data-testid="firebase-confirm-password-input"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none"
                tabIndex={-1}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm space-y-2" role="alert">
            <div>{error}</div>
            {unverifiedEmail && (
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={isResending || resendSecondsLeft > 0}
                className="text-green-700 dark:text-green-300 hover:text-green-800 dark:hover:text-green-200 disabled:text-gray-400 dark:disabled:text-gray-500 font-medium underline disabled:no-underline"
                data-testid="resend-verification-button"
              >
                {isResending
                  ? 'Sending…'
                  : resendSecondsLeft > 0
                    ? `Resend available in ${resendSecondsLeft}s`
                    : 'Resend verification email'}
              </button>
            )}
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300 text-sm" role="status">
            {successMessage}
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-2.5 px-4 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-medium rounded-lg transition-colors flex items-center justify-center"
          data-testid="firebase-submit-button"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              {mode === 'signin' ? 'Signing in...' : mode === 'signup' ? 'Creating account...' : 'Sending...'}
            </>
          ) : (
            <>
              {mode === 'signin' && 'Sign in with Email'}
              {mode === 'signup' && 'Create Account'}
              {mode === 'reset' && 'Send Reset Email'}
            </>
          )}
        </button>

        {/* Mode Switchers */}
        <div className="text-center text-sm text-gray-600 dark:text-gray-400 space-y-2">
          {mode === 'signin' && (
            <>
              <p>
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={() => switchMode('signup')}
                  className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 font-medium"
                  data-testid="switch-to-signup"
                >
                  Sign up
                </button>
              </p>
              <p>
                <button
                  type="button"
                  onClick={() => switchMode('reset')}
                  className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 font-medium"
                  data-testid="switch-to-reset"
                >
                  Forgot password?
                </button>
              </p>
            </>
          )}
          {mode === 'signup' && (
            <p>
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => switchMode('signin')}
                className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 font-medium"
                data-testid="switch-to-signin"
              >
                Sign in
              </button>
            </p>
          )}
          {mode === 'reset' && (
            <p>
              Remember your password?{' '}
              <button
                type="button"
                onClick={() => switchMode('signin')}
                className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 font-medium"
                data-testid="switch-to-signin"
              >
                Sign in
              </button>
            </p>
          )}
        </div>
      </form>
    </div>
  );
};

export default FirebaseLogin;
